/**
 * The store's portal as governed reads (D533, AN-MERCHANT-V2; research doc 05 section 3.1-3.3 rows with audience MER).
 *
 * A store does NOT send a query. It asks for one of a fixed set of INSIGHTS (a quality scorecard, loss by shift, logistics, new against returning, items,
 * several stores, reconciliation, availability) and a FILTER STATE (period, locations, channel, payer, shifts, weekdays). This module turns that pair into
 * the governed requests the server runs - metrics and dimensions are fixed here, the filters are the only free part and are all inclusion operators on a
 * closed vocabulary - and each request goes through `runPartnerQuery` (D528): the tenant forced by the server, the k floor on records and people, the
 * per-value and complement guards, one audit row before release, a per-tenant rate limit. The caller never names a metric, a dimension, an operator or a
 * tenant, so the partner wall (`planPartner`, `merchant-review.ts`) is the second lock on a door that is already narrow.
 *
 * Pure except {@link runMerchantInsight}, whose effects (the executor, the audit, the limiter) are injected; the API runs it over PostgreSQL in one
 * read-only transaction and the fake API over the in-memory evaluator, so the two cannot drift.
 */
import { BUCKET_EDGES } from './buckets.js';
import { addDays, firstOfMonth, inclusiveDays, isRealDate, lastOfMonth } from './dates.js';
import { runPartnerQuery } from './partner-run.js';
import { isSuppressedRow } from './partner-suppression.js';
export const MERCHANT_INSIGHTS = ['scorecard', 'shifts', 'logistics', 'customers', 'items', 'stores', 'reconciliation', 'availability'];
export const isMerchantInsight = (v) => typeof v === 'string' && MERCHANT_INSIGHTS.includes(v);
export const MERCHANT_PORTAL_LIMITS = {
    /** The widest period one view spans, in local days (the dashboard's own bound). */
    maxDays: 93,
    maxLocations: 20,
    /** The per-tenant budget of reads a minute (each read of an insight takes one turn): a screen of eight tabs is a handful of reads each. */
    readsPerMinute: 90,
};
export const FILTER_KEYS = ['location', 'channel', 'payer', 'shift', 'weekday'];
/** The closed vocabularies a filter can carry. */
export const PORTAL_CHANNELS = ['APP', 'PARTNER_DESK'];
/** Who paid, in the ETL's words (`dim_payment.payer_kind` of an earnings booking): the guest at the counter, or a partner company's account. */
export const PORTAL_PAYERS = ['GUEST', 'PARTNER_ACCOUNT'];
export const PORTAL_SHIFTS = Array.from({ length: 12 }, (_, i) => String(i).padStart(2, '0'));
export const PORTAL_WEEKDAYS = ['1', '2', '3', '4', '5', '6', '7'];
/** A location reference as the catalog writes it: letters, digits and . _ : - (a slug or an id), never punctuation that means something elsewhere. */
const REF = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/;
const listOf = (v) => {
    if (v === undefined || v === null || v === '')
        return [];
    const parts = [];
    const push = (x) => {
        if (typeof x !== 'string')
            return false;
        for (const p of x.split(','))
            if (p.trim() !== '')
                parts.push(p.trim());
        return true;
    };
    if (Array.isArray(v)) {
        for (const x of v)
            if (!push(x))
                return undefined;
    }
    else if (!push(v))
        return undefined;
    return [...new Set(parts)];
};
/** Validate a raw query string / body into a filter state: every value is checked against a closed vocabulary or a bounded shape. */
export function parseMerchantFilters(raw) {
    const from = typeof raw.from === 'string' ? raw.from.trim() : undefined;
    const to = typeof raw.to === 'string' && raw.to.trim() !== '' ? raw.to.trim() : from;
    if (from === undefined || to === undefined || !isRealDate(from) || !isRealDate(to) || to < from)
        return { ok: false, code: 'PORTAL_PERIOD_INVALID', field: 'from' };
    if (inclusiveDays(from, to) > MERCHANT_PORTAL_LIMITS.maxDays)
        return { ok: false, code: 'PORTAL_PERIOD_TOO_LONG', field: 'to' };
    const closed = (name, v, allowed, max) => {
        const l = listOf(v);
        if (l === undefined || l.length > max || l.some((x) => !allowed.includes(x)))
            return { ok: false, code: 'PORTAL_FILTER_INVALID', field: name };
        return { ok: true, values: l };
    };
    const channels = closed('channel', raw.channel ?? raw.channels, PORTAL_CHANNELS, 2);
    if (!channels.ok)
        return channels;
    const payers = closed('payer', raw.payer ?? raw.payers, PORTAL_PAYERS, 2);
    if (!payers.ok)
        return payers;
    const shifts = closed('shift', raw.shift ?? raw.shifts, PORTAL_SHIFTS, 12);
    if (!shifts.ok)
        return shifts;
    const weekdays = closed('weekday', raw.weekday ?? raw.weekdays, PORTAL_WEEKDAYS, 7);
    if (!weekdays.ok)
        return weekdays;
    const locations = listOf(raw.location ?? raw.locations);
    if (locations === undefined || locations.length > MERCHANT_PORTAL_LIMITS.maxLocations || locations.some((x) => !REF.test(x)))
        return { ok: false, code: 'PORTAL_FILTER_INVALID', field: 'location' };
    const grain = raw.grain === undefined || raw.grain === '' ? 'shift' : raw.grain;
    if (grain !== 'shift' && grain !== 'day_part')
        return { ok: false, code: 'PORTAL_FILTER_INVALID', field: 'grain' };
    return { ok: true, state: { from, to, locations, channels: channels.values, payers: payers.values, shifts: shifts.values, weekdays: weekdays.values, grain } };
}
/** The filters each source can carry (the planner refuses a condition the source has no column for). */
const CARRIES = {
    fact_order: ['location', 'channel', 'shift', 'weekday'],
    fact_money: ['location', 'payer', 'shift', 'weekday'],
    fact_order_item: ['location', 'shift', 'weekday'],
    fact_issue: ['location', 'shift', 'weekday'],
    fact_store_availability: ['location', 'shift', 'weekday'],
};
const conditionsOf = (state, source, only) => {
    const carries = CARRIES[source];
    const want = [
        ['location', 'location', state.locations],
        ['channel', 'channel', state.channels],
        ['payer', 'payer_kind', state.payers],
        ['shift', 'shift', state.shifts],
        ['weekday', 'weekday', state.weekdays],
    ];
    const filters = [];
    const ignored = [];
    for (const [key, dimension, values] of want) {
        if (values.length === 0)
            continue;
        if (!carries.includes(key) || (only !== undefined && !only.includes(key)))
            ignored.push(key);
        else
            filters.push({ dimension, op: 'in', values: [...values] });
    }
    return { filters, ignored };
};
/** The metrics of the scorecard's core read (12 is the planner's cap). */
export const SCORECARD_CORE_METRICS = [
    'orders', 'orders_completed', 'store_acceptance_rate', 'store_refusal_rate', 'store_cancel_rate', 'refused_orders', 'auto_refused_orders', 'store_cancelled_orders',
    'accept_time', 'prep_time', 'pickup_wait', 'cancellation_rate',
];
/** The reads of one insight under one filter state. */
export function merchantInsightReads(insight, state) {
    const period = { from: state.from, to: state.to };
    const spec = (id, source, body, options = {}) => {
        const { filters, ignored } = conditionsOf(state, source, options.only);
        return { id, source, floor: options.floor ?? 'k', ignored, ...(options.period !== undefined ? { period: options.period } : {}), body: { ...body, period: options.period ?? period, ...(filters.length > 0 ? { filters } : {}), limit: 1000 } };
    };
    switch (insight) {
        case 'scorecard':
            return [
                spec('core', 'fact_order', { metrics: [...SCORECARD_CORE_METRICS], compare: 'previous_period' }),
                spec('issues', 'fact_issue', { metrics: ['issues', 'refunds'] }),
                spec('issue_reasons', 'fact_issue', { metrics: ['issues'], dimensions: ['issue_reason'] }),
                spec('availability', 'fact_store_availability', { metrics: ['store_availability_rate', 'store_live_seconds', 'store_open_seconds', 'store_paused_seconds', 'store_pauses'] }, { floor: 'none' }),
            ];
        case 'shifts':
            return [
                spec('heat', 'fact_order', { metrics: ['orders_cancelled', 'lost_order_value', 'refused_order_value'], dimensions: [state.grain === 'shift' ? 'shift' : 'day_part', 'weekday'] }),
                spec('reasons', 'fact_order', { metrics: ['orders_cancelled', 'lost_order_value'], dimensions: ['cancel_reason'] }),
                spec('actors', 'fact_order', { metrics: ['orders_cancelled', 'lost_order_value'], dimensions: ['cancel_actor'] }),
            ];
        case 'logistics':
            return [
                spec('means', 'fact_order', { metrics: ['accept_time', 'prep_time', 'pickup_wait', 'trip_time', 'delivery_time', 'orders_completed'], compare: 'previous_period' }),
                spec('prep', 'fact_order', { metrics: ['orders_completed'], dimensions: ['prep_time_bucket'] }),
                spec('wait', 'fact_order', { metrics: ['orders_completed'], dimensions: ['pickup_wait_bucket'] }),
                spec('transit', 'fact_order', { metrics: ['orders_completed'], dimensions: ['delivery_time_bucket'] }),
                spec('total', 'fact_order', { metrics: ['orders_completed'], dimensions: ['total_time_bucket'] }),
            ];
        case 'customers': {
            // New against returning is a property of a row and its calendar month (the planner enforces it for a store): the cells are MONTHLY, over WHOLE months
            // (the period is widened to them and says so), with no cut by weekday or shift - narrowing a month cell to a weekday is the day-level cell again.
            const months = { from: firstOfMonth(state.from), to: lastOfMonth(state.to) };
            const series = spec('series', 'fact_order', { metrics: ['orders_completed'], dimensions: ['merchant_customer_type'], granularity: 'month' }, { only: ['location', 'channel'], period: months });
            // The whole-period split is NOT a second query: it is the sum of the series' released cells, and only when the series withheld none (a total next to a
            // series with a pooled cell would undo the pool by subtraction).
            return [series, { id: 'split', source: 'fact_order', floor: 'k', ignored: series.ignored, period: months, derive: { kind: 'customer_split', from: 'series' }, body: {} }];
        }
        case 'items':
            return [
                spec('pareto', 'fact_order_item', { metrics: ['sold_item_quantity', 'sold_item_revenue'], dimensions: ['owned_item'], sort: [{ by: 'sold_item_revenue', direction: 'desc' }] }),
                spec('categories', 'fact_order_item', { metrics: ['sold_item_quantity', 'sold_item_revenue'], dimensions: ['menu_category'] }),
                spec('basket', 'fact_order', { metrics: ['basket_size', 'orders_completed'] }),
            ];
        case 'stores':
            return [
                spec('total', 'fact_order', { metrics: ['orders', 'orders_completed', 'cancellation_rate', 'store_acceptance_rate', 'prep_time', 'delivery_time'] }),
                spec('by_location', 'fact_order', { metrics: ['orders', 'orders_completed', 'cancellation_rate', 'store_acceptance_rate', 'prep_time', 'delivery_time'], dimensions: ['location'] }),
                spec('money_total', 'fact_money', { metrics: ['merchandise', 'partner_net'] }),
                spec('money_by_location', 'fact_money', { metrics: ['merchandise', 'partner_net'], dimensions: ['location'] }),
            ];
        case 'reconciliation':
            return [spec('statement', 'fact_money', { metrics: ['gmv', 'merchandise', 'delivery_fees', 'discounts', 'commission', 'partner_net'] })];
        case 'availability':
            return [
                spec('daily', 'fact_store_availability', { metrics: ['store_open_seconds', 'store_paused_seconds', 'store_live_seconds', 'store_pauses'], granularity: 'day' }, { floor: 'none' }),
                spec('states', 'fact_store_availability', { metrics: ['store_live_seconds', 'store_pauses'], dimensions: ['availability_state'] }, { floor: 'none' }),
            ];
    }
}
/** The reads the benchmark's own side runs: the store over the period, nothing else (the peers are the whole store too). */
export const BENCHMARK_OWN_METRICS = ['orders', 'completion_rate', 'cancellation_rate', 'prep_time', 'delivery_time'];
export function benchmarkOwnRead(state, month) {
    const period = month ?? { from: state.from, to: state.to };
    return { id: 'own', source: 'fact_order', floor: 'k', ignored: FILTER_KEYS.filter((k) => (k === 'location' ? state.locations.length > 0 : k === 'channel' ? state.channels.length > 0 : k === 'payer' ? state.payers.length > 0 : k === 'shift' ? state.shifts.length > 0 : state.weekdays.length > 0)), period, body: { metrics: [...BENCHMARK_OWN_METRICS], period: { from: period.from, to: period.to }, limit: 10 } };
}
const edgesOf = (dimensions) => {
    const edges = {};
    for (const d of dimensions)
        if (d in BUCKET_EDGES && d !== 'shift')
            edges[d] = BUCKET_EDGES[d];
    return Object.keys(edges).length > 0 ? { edges } : {};
};
export function toWireRead(spec, result) {
    const rows = result.rows.map((r) => {
        if (isSuppressedRow(r)) {
            const m = r;
            return { suppressed: true, reasonCode: m.reasonCode, kThreshold: m.kThreshold, withheldRows: m.withheldRows, ...(m.currency !== undefined ? { currency: m.currency } : {}) };
        }
        const row = r;
        return { keys: row.keys, ...(row.currency !== undefined ? { currency: row.currency } : {}), values: row.values, ...(row.baseline !== undefined ? { baseline: row.baseline } : {}) };
    });
    return {
        id: spec.id,
        metrics: result.metrics.map((m) => ({ id: m.id, unit: m.unit, currencyBound: m.currencyBound })),
        dimensions: result.dimensions.map((d) => d.id),
        rows,
        ...(result.totals !== undefined ? { totals: result.totals } : {}),
        ...(result.totalsWithheld === true ? { totalsWithheld: true } : {}),
        ...(result.baselineWithheld === true ? { baselineWithheld: true } : {}),
        ...(result.withheldAll !== undefined ? { withheldAll: result.withheldAll } : {}),
        kThreshold: result.kThreshold,
        ...edgesOf(result.dimensions.map((d) => d.id)),
        ignored: spec.ignored,
        floor: spec.floor,
        compared: result.comparison !== undefined && result.baselineWithheld !== true,
        ...(spec.period !== undefined ? { period: spec.period } : {}),
    };
}
/**
 * The whole-period new / returning split, from the released cells of the monthly series and from nothing else. When the series withheld ANY cell (or the
 * whole answer) the split is withheld too: the sum of the released cells would understate the truth without saying so, and the true total would let the
 * pooled cells be recovered by subtraction.
 */
export function deriveCustomerSplit(spec, series) {
    const base = { id: spec.id, metrics: series.metrics.map((m) => ({ id: m.id, unit: m.unit, currencyBound: m.currencyBound })), dimensions: ['merchant_customer_type'], kThreshold: series.kThreshold, ignored: spec.ignored, floor: spec.floor, compared: false, ...(spec.period !== undefined ? { period: spec.period } : {}) };
    if (series.withheldAll !== undefined)
        return { ...base, rows: [], withheldAll: series.withheldAll };
    const markers = series.rows.filter(isSuppressedRow);
    if (markers.length > 0) {
        const first = markers[0];
        return { ...base, rows: [{ suppressed: true, reasonCode: first.reasonCode, kThreshold: series.kThreshold, withheldRows: markers.reduce((a, m) => a + m.withheldRows, 0) }] };
    }
    const sums = new Map();
    for (const r of series.rows) {
        if (isSuppressedRow(r))
            continue;
        const type = r.keys.merchant_customer_type?.key;
        const n = Number(r.values.orders_completed?.value ?? 0);
        if (type === null || type === undefined || !Number.isFinite(n))
            continue;
        sums.set(type, (sums.get(type) ?? 0) + n);
    }
    const rows = ['NEW', 'RETURNING'].filter((t) => sums.has(t)).map((t) => ({ keys: { merchant_customer_type: { key: t } }, values: { orders_completed: { value: sums.get(t) } } }));
    return { ...base, rows };
}
/**
 * Run the reads of an insight, in order, each through {@link runPartnerQuery}. A rate limit or an audit failure stops the insight and releases NOTHING (a
 * half-released screen would be a way around the audit); any other refusal (a request the planner cannot plan for this tenant) is that read's own state.
 */
export async function runMerchantInsight(input) {
    const reads = {};
    const results = {};
    for (const spec of input.reads) {
        if (spec.derive !== undefined) {
            const from = results[spec.derive.from];
            const source = reads[spec.derive.from];
            if (from === undefined)
                reads[spec.id] = { id: spec.id, refused: source !== undefined && 'refused' in source ? source.refused : 'DERIVED_READ_MISSING', ignored: spec.ignored };
            else
                reads[spec.id] = deriveCustomerSplit(spec, from);
            continue;
        }
        const run = await runPartnerQuery({
            body: spec.body,
            tenant: input.tenant,
            // the people floor is relaxed only on a source with no customer in it (the planner decides which; a source with one never is)
            ctx: { ...input.ctx, subjectsOptional: true },
            execute: input.execute,
            audit: input.audit,
            limiter: input.limiter,
            floor: spec.floor,
            ...(input.k !== undefined ? { k: input.k } : {}),
        });
        if (!run.ok) {
            if (run.code === 'PARTNER_RATE_LIMITED' || run.code === 'PARTNER_AUDIT_UNAVAILABLE')
                return { ok: false, code: run.code, ...(run.retryAfterSeconds !== undefined ? { retryAfterSeconds: run.retryAfterSeconds } : {}) };
            reads[spec.id] = { id: spec.id, refused: run.code, ignored: spec.ignored };
            continue;
        }
        results[spec.id] = run.result;
        reads[spec.id] = toWireRead(spec, run.result);
    }
    return { ok: true, reads, results };
}
// ───────────────────────────── small readers shared by the folds ─────────────────────────────
/** The first released row of a read, or undefined when the whole read (or its first row) is withheld or refused. */
export function firstRow(read) {
    if (read === undefined || 'refused' in read || read.withheldAll !== undefined)
        return undefined;
    const row = read.rows[0];
    return row === undefined || 'suppressed' in row ? undefined : row;
}
export function cellValue(row, metric, side = 'current') {
    const cell = (side === 'current' ? row?.values : row?.baseline)?.[metric];
    if (cell === undefined || cell.value === null || cell.value === undefined)
        return null;
    const n = Number(cell.value);
    return Number.isFinite(n) ? n : null;
}
/** The previous period a comparison read lines up with (the same length, ending the day before). */
export function previousPeriod(from, to) {
    const days = inclusiveDays(from, to);
    return { from: addDays(from, -days), to: addDays(from, -1) };
}
//# sourceMappingURL=merchant-insights.js.map